import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type ListBookingPagesResult, type MyAvailabilityResult } from '@whinerp/client';
import { Button, Panel, SectionLabel } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';
import { useSession } from '../../app/session.js';

/**
 * Your working hours and your booking pages (G-187). The hours are what every
 * booking page of yours offers times from; the page is the address you hand
 * out.
 */

const INPUT = 'h-input rounded-control border border-control bg-surface px-2 text-body text-ink';
const DAYS = [0, 1, 2, 3, 4, 5, 6] as const;

type Day = { on: boolean; from: string; to: string };

export function BookingPagesPanel(): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const queryClient = useQueryClient();
  const { session } = useSession();
  const hours = useQuery<MyAvailabilityResult, ApiError>({ queryKey: ['availability'], queryFn: () => api.myAvailability({}) });
  const pages = useQuery<ListBookingPagesResult, ApiError>({ queryKey: ['booking-pages'], queryFn: () => api.listBookingPages({}) });
  const [week, setWeek] = useState<Record<number, Day>>({});
  const [slotMinutes, setSlotMinutes] = useState(30);
  const [draft, setDraft] = useState({ slug: '', title: '' });

  useEffect(() => {
    if (!hours.data) return;
    const next: Record<number, Day> = {};
    for (const day of DAYS) {
      const rule = hours.data.hours.find((entry) => entry.weekday === day);
      next[day] = rule ? { on: true, from: rule.startsAt, to: rule.endsAt } : { on: false, from: '09:00', to: '17:00' };
    }
    setWeek(next);
    if (hours.data.hours[0]) setSlotMinutes(hours.data.hours[0].slotMinutes);
  }, [hours.data]);

  const saveHours = useMutation({
    mutationFn: () =>
      api.setMyAvailability({
        body: {
          hours: DAYS.filter((day) => week[day]?.on).map((day) => ({ weekday: day, startsAt: week[day]!.from, endsAt: week[day]!.to, slotMinutes })),
        },
      }),
    onSuccess: (result) => queryClient.setQueryData(['availability'], result),
  });
  const savePage = useMutation({
    mutationFn: () => api.saveBookingPage({ body: { pageId: null, slug: draft.slug, title: draft.title, userId: session.user.id, active: true } }),
    onSuccess: () => {
      setDraft({ slug: '', title: '' });
      void queryClient.invalidateQueries({ queryKey: ['booking-pages'] });
    },
  });

  if (!hours.data || !pages.data) return null;
  const origin = typeof window === 'undefined' ? '' : window.location.origin;

  return (
    <Panel data-region="booking-pages">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('bookingPages.title')}</SectionLabel>
        <p className="text-meta text-ink-meta">{t('bookingPages.help')}</p>
        <div className="flex flex-col gap-1" data-availability>
          {DAYS.map((day) => {
            const value = week[day] ?? { on: false, from: '09:00', to: '17:00' };
            return (
              <div key={day} className="flex flex-wrap items-center gap-2 text-dense" data-availability-day={day}>
                <label className="flex w-28 items-center gap-2 text-ink">
                  <input type="checkbox" data-day-on checked={value.on} onChange={(event) => setWeek({ ...week, [day]: { ...value, on: event.target.checked } })} />
                  {t(`bookingPages.day.${String(day) as '0'}`)}
                </label>
                <input className={`${INPUT} w-28`} type="time" data-day-from disabled={!value.on} value={value.from} aria-label={t('bookingPages.from')}
                  onChange={(event) => setWeek({ ...week, [day]: { ...value, from: event.target.value } })} />
                <input className={`${INPUT} w-28`} type="time" data-day-to disabled={!value.on} value={value.to} aria-label={t('bookingPages.to')}
                  onChange={(event) => setWeek({ ...week, [day]: { ...value, to: event.target.value } })} />
              </div>
            );
          })}
          <label className="flex items-center gap-2 text-dense text-ink">
            {t('bookingPages.length')}
            <select className={INPUT} data-slot-minutes value={slotMinutes} onChange={(event) => setSlotMinutes(Number(event.target.value))}>
              {[15, 30, 45, 60].map((minutes) => <option key={minutes} value={minutes}>{t('bookingPages.minutes', { values: { n: String(minutes) } })}</option>)}
            </select>
          </label>
          <div>
            <Button size="small" data-availability-save disabled={saveHours.isPending} onClick={() => saveHours.mutate()}>{t('bookingPages.saveHours')}</Button>
          </div>
        </div>

        {pages.data.pages.length > 0 ? (
          <ul className="flex flex-col gap-1 text-dense" data-booking-page-list>
            {pages.data.pages.map((page) => (
              <li key={page.pageId} data-booking-page={page.pageId} className="flex flex-wrap gap-2">
                <span className="text-ink">{page.title}</span>
                <span className="text-ink-meta">{page.ownerName}</span>
                <a className="text-bronze underline" data-booking-page-link href={`/b/${pages.data.workspace}/${page.slug}`}>
                  {`${origin}/b/${pages.data.workspace}/${page.slug}`}
                </a>
              </li>
            ))}
          </ul>
        ) : null}
        <form className="flex flex-wrap items-end gap-2" data-booking-page-form
          onSubmit={(event) => {
            event.preventDefault();
            savePage.mutate();
          }}>
          <label className="flex flex-col gap-1 text-meta text-ink-meta">
            {t('bookingPages.pageTitle')}
            <input className={INPUT} required maxLength={120} data-booking-page-title value={draft.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} />
          </label>
          <label className="flex flex-col gap-1 text-meta text-ink-meta">
            {t('bookingPages.address')}
            <input className={INPUT} required maxLength={63} data-booking-page-slug placeholder="intro-call" value={draft.slug} onChange={(event) => setDraft({ ...draft, slug: event.target.value })} />
          </label>
          <Button size="small" type="submit" data-booking-page-create disabled={savePage.isPending}>{t('bookingPages.create')}</Button>
        </form>
        {[saveHours.error, savePage.error].map((error, index) => (error ? <p key={index} className="text-dense text-danger-text">{errorText(error as ApiError)}</p> : null))}
      </div>
    </Panel>
  );
}
